import React, { useState } from 'react';
import { Box, Paper, Typography, CircularProgress, Chip, IconButton, Slider, Button } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import TranslateIcon from '@mui/icons-material/Translate';
import { TextField } from '@mui/material';
import { OpenAIService } from '../services/openai';
import { GoogleSTTService } from '../services/googleSTT';
import { AnthropicSTTService } from '../services/anthropicSTT';
import DeleteIcon from '@mui/icons-material/Delete';

interface TranscriptionResult {
  text: string;
  model: string;
  duration?: number;
  confidence?: number;
  processingTime?: number;
  type: "transcribe" | "translate";
  provider?: string;
  error?: boolean;
  isTextTranslation?: boolean;
}

interface QualityEvaluation {
  score: number;  // 0-1 score
  explanation: string;
}

interface TranscriptionResultsProps {
  results: TranscriptionResult[];
  setResults: React.Dispatch<React.SetStateAction<TranscriptionResult[]>>;
  onClear: () => void;
  isLoading: boolean;
  audioBlob?: Blob;
}

const TranscriptionResults: React.FC<TranscriptionResultsProps> = ({ results, setResults, onClear, isLoading, audioBlob }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const [referenceText, setReferenceText] = useState('');
  const [translationReferenceText, setTranslationReferenceText] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  const [qualityEvaluations, setQualityEvaluations] = useState<Record<string, QualityEvaluation>>({});

  const formatTime = (seconds: number) => {
    if (!isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  React.useEffect(() => {
    if (audioBlob) {
      const url = URL.createObjectURL(audioBlob);
      const audio = new Audio(url);
      audioRef.current = audio;
      
      const setAudioData = () => {
        if (isFinite(audio.duration)) {
          setDuration(audio.duration);
        }
      };
      
      // Add more event listeners to ensure we catch the duration
      audio.addEventListener('loadedmetadata', setAudioData);
      audio.addEventListener('loadeddata', setAudioData);
      audio.addEventListener('durationchange', setAudioData);
      
      audio.ontimeupdate = () => {
        if (audio.currentTime) {
          setCurrentTime(audio.currentTime);
        }
      };
      
      audio.onended = () => {
        setIsPlaying(false);
        setCurrentTime(0);
      };

      // Force load the audio
      audio.load();
      
      // If the audio is already loaded, set the duration
      if (audio.readyState >= 2) {
        setAudioData();
      }

      return () => {
        audio.removeEventListener('loadedmetadata', setAudioData);
        audio.removeEventListener('loadeddata', setAudioData);
        audio.removeEventListener('durationchange', setAudioData);
        audio.pause();
        setIsPlaying(false);
        URL.revokeObjectURL(url);
      };
    }
  }, [audioBlob]);

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(error => {
          console.error('Error playing audio:', error);
        });
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeChange = (_event: Event, newValue: number | number[]) => {
    if (audioRef.current && typeof newValue === 'number') {
      audioRef.current.currentTime = newValue;
      setCurrentTime(newValue);
    }
  };

  const evaluateQuality = async (reference: string, hypothesis: string, type: 'transcribe' | 'translate'): Promise<QualityEvaluation> => {
    try {
      const openaiApiKey = import.meta.env.VITE_OPENAI_API_KEY;
      const openaiService = new OpenAIService({ apiKey: openaiApiKey });
      
      const prompt = type === 'transcribe' 
        ? `You are a transcription quality evaluator. Compare the following transcription with the reference and provide a score and brief explanation.

Rules:
- Score from 0 to 1 (1 = perfect match)
- Traditional and simplified Chinese characters should be treated as equivalent (e.g., 學 = 学)
- Punctuation differences should have minimal impact on the score
- Focus only on character accuracy, treating traditional/simplified variants as the same
- Keep explanation concise (max 2 sentences)

Respond in this JSON format only:
{
  "score": number,
  "explanation": "brief explanation"
}

Reference transcription: "${reference}"
Transcription to evaluate: "${hypothesis}"`
        : `You are a translation quality evaluator. Compare the following translation with the reference and provide a score and brief explanation.

Rules:
- Score from 0 to 1 (1 = perfect match)
- For translations to English: Score MUST be 0 if the output is not in English
- For translations to Chinese: Score MUST be 0 if the output is not in Chinese
- After language check, evaluate meaning accuracy and natural expression
- Speaker labels (e.g., "Speaker A:", "John:") should NOT affect the score
- Before scoring, strip speaker labels from both texts and compare the actual content
- Example: "Speaker A: Hello" and "Hello" should be considered identical
- Keep explanation concise (max 2 sentences)

Process:
1. First strip all speaker labels (e.g., "Speaker A:", "John:", etc.) from both texts
2. Compare the remaining content for meaning and accuracy
3. Score based on semantic equivalence of the actual content
4. Do NOT penalize for presence or absence of speaker labels

Respond in this JSON format only:
{
  "score": number,
  "explanation": "brief explanation"
}

Reference translation: "${reference}"
Translation to evaluate: "${hypothesis}"`;

      const result = await openaiService.translateText(prompt, "en", "gpt-4o-mini");
      const evaluation = JSON.parse(result.text);
      return {
        score: evaluation.score,
        explanation: evaluation.explanation
      };
    } catch (error) {
      console.error('Quality evaluation error:', error);
      return {
        score: 0,
        explanation: 'Error evaluating quality'
      };
    }
  };

  const handleReferenceChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newText = event.target.value;
    setReferenceText(newText);
    
    if (!newText.trim()) {
      setQualityEvaluations({});
      return;
    }

    // Evaluate each transcription result
    const newQualities: Record<string, QualityEvaluation> = { ...qualityEvaluations };
    for (const result of results) {
      if (result.type === 'transcribe') {
        newQualities[result.model] = await evaluateQuality(newText, result.text, 'transcribe');
      }
    }
    setQualityEvaluations(newQualities);
  };

  const handleTranslationReferenceChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newText = event.target.value;
    setTranslationReferenceText(newText);
    
    if (!newText.trim()) {
      setQualityEvaluations({});
      return;
    }

    // Evaluate each translation result
    const newQualities: Record<string, QualityEvaluation> = { ...qualityEvaluations };
    for (const result of results) {
      if (result.type === 'translate') {
        newQualities[result.model] = await evaluateQuality(newText, result.text, 'translate');
      }
    }
    setQualityEvaluations(newQualities);
  };

  const handleTranslateClick = async () => {
    if (!referenceText) return;

    setIsTranslating(true);

    try {
      const newResults: TranscriptionResult[] = [];

      // Text-based translations
      try {
        const anthropicApiKey = import.meta.env.VITE_ANTHROPIC_API_KEY;
        if (!anthropicApiKey) {
          throw new Error("Anthropic API key not found");
        }
        const anthropic = new AnthropicSTTService(anthropicApiKey);
        const claudeResult = await anthropic.translateText(referenceText);
        newResults.push({
          text: claudeResult.text,
          model: "Anthropic Claude 3.7 Sonnet Translate",
          processingTime: claudeResult.duration,
          type: "translate",
          provider: "anthropic",
          isTextTranslation: true
        });
      } catch (error) {
        console.error("Anthropic Claude translation failed:", error);
        newResults.push({
          text: error instanceof Error ? error.message : "Unknown error occurred",
          model: "Anthropic Claude 3.7 Sonnet Translate",
          processingTime: 0,
          type: "translate",
          provider: "anthropic",
          error: true,
          isTextTranslation: true
        });
      }

      // OpenAI GPT-4o Mini
      try {
        const openaiApiKey = import.meta.env.VITE_OPENAI_API_KEY;
        if (!openaiApiKey) {
          throw new Error("OpenAI API key not found");
        }
        const openai = new OpenAIService({ apiKey: openaiApiKey });
        const gpt4oMiniResult = await openai.translateText(referenceText, "en", "gpt-4o-mini");
        newResults.push({
          text: gpt4oMiniResult.text,
          model: "OpenAI GPT-4o Mini Translate",
          processingTime: gpt4oMiniResult.duration,
          type: "translate",
          provider: "openai",
          isTextTranslation: true
        });
      } catch (error) {
        console.error("OpenAI GPT-4o Mini translation failed:", error);
        newResults.push({
          text: error instanceof Error ? error.message : "Unknown error occurred",
          model: "OpenAI GPT-4o Mini Translate",
          processingTime: 0,
          type: "translate",
          provider: "openai",
          error: true,
          isTextTranslation: true
        });
      }

      // OpenAI GPT-4o
      try {
        const openaiApiKey = import.meta.env.VITE_OPENAI_API_KEY;
        if (!openaiApiKey) {
          throw new Error("OpenAI API key not found");
        }
        const openai = new OpenAIService({ apiKey: openaiApiKey });
        const gpt4oResult = await openai.translateText(referenceText, "en", "gpt-4o");
        newResults.push({
          text: gpt4oResult.text,
          model: "OpenAI GPT-4o Translate",
          processingTime: gpt4oResult.duration,
          type: "translate",
          provider: "openai",
          isTextTranslation: true
        });
      } catch (error) {
        console.error("OpenAI GPT-4o translation failed:", error);
        newResults.push({
          text: error instanceof Error ? error.message : "Unknown error occurred",
          model: "OpenAI GPT-4o Translate",
          processingTime: 0,
          type: "translate",
          provider: "openai",
          error: true,
          isTextTranslation: true
        });
      }

      // Google Gemini Flash
      try {
        const googleApiKey = import.meta.env.VITE_GOOGLE_API_KEY;
        if (!googleApiKey) {
          throw new Error("Google API key not found");
        }
        const google = new GoogleSTTService(googleApiKey, "gemini-2.0-flash");
        const geminiFlashResult = await google.translateText(referenceText);
        newResults.push({
          text: geminiFlashResult.text,
          model: "Google Gemini 2.0 Flash Translate",
          processingTime: geminiFlashResult.duration,
          type: "translate",
          provider: "google",
          isTextTranslation: true
        });
      } catch (error) {
        console.error("Google Gemini Flash translation failed:", error);
        newResults.push({
          text: error instanceof Error ? error.message : "Unknown error occurred",
          model: "Google Gemini 2.0 Flash Translate",
          processingTime: 0,
          type: "translate",
          provider: "google",
          error: true,
          isTextTranslation: true
        });
      }

      // Google Gemini Flash Lite
      try {
        const googleApiKey = import.meta.env.VITE_GOOGLE_API_KEY;
        if (!googleApiKey) {
          throw new Error("Google API key not found");
        }
        const googleLite = new GoogleSTTService(googleApiKey, "gemini-2.0-flash-lite");
        const geminiFlashLiteResult = await googleLite.translateText(referenceText);
        newResults.push({
          text: geminiFlashLiteResult.text,
          model: "Google Gemini 2.0 Flash Lite Translate",
          processingTime: geminiFlashLiteResult.duration,
          type: "translate",
          provider: "google",
          isTextTranslation: true
        });
      } catch (error) {
        console.error("Google Gemini Flash Lite translation failed:", error);
        newResults.push({
          text: error instanceof Error ? error.message : "Unknown error occurred",
          model: "Google Gemini 2.0 Flash Lite Translate",
          processingTime: 0,
          type: "translate",
          provider: "google",
          error: true,
          isTextTranslation: true
        });
      }

      // Google Gemini 2.5 Pro
      try {
        const googleApiKey = import.meta.env.VITE_GOOGLE_API_KEY;
        if (!googleApiKey) {
          throw new Error("Google API key not found");
        }
        const googlePro = new GoogleSTTService(googleApiKey, "gemini-2.5-pro-exp-03-25");
        const gemini25ProResult = await googlePro.translateText(referenceText);
        newResults.push({
          text: gemini25ProResult.text,
          model: "Google Gemini 2.5 Pro Experimental Translate",
          processingTime: gemini25ProResult.duration,
          type: "translate",
          provider: "google",
          isTextTranslation: true
        });
      } catch (error) {
        console.error("Google Gemini 2.5 Pro translation failed:", error);
        newResults.push({
          text: error instanceof Error ? error.message : "Unknown error occurred",
          model: "Google Gemini 2.5 Pro Experimental Translate",
          processingTime: 0,
          type: "translate",
          provider: "google",
          error: true,
          isTextTranslation: true
        });
      }

      // Update results: preserve transcriptions and audio translations, add/update text translations
      setResults((prevResults: TranscriptionResult[]) => {
        // Keep all transcriptions and audio translations
        const preservedResults = prevResults.filter(r => 
          r.type === 'transcribe' || 
          (r.type === 'translate' && !r.isTextTranslation)
        );
        
        // Always add all new text translations
        return [...preservedResults, ...newResults];
      });
    } catch (error) {
      console.error("Translation error:", error);
    } finally {
      setIsTranslating(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (results.length === 0) {
    return null;
  }

  const renderResults = (type: 'transcribe' | 'translate', isTextTranslation?: boolean) => {
    console.log('Rendering results for:', { type, isTextTranslation });
    console.log('All results:', results);
    const filteredResults = results.filter((result: TranscriptionResult) => 
      result.type === type && 
      (type === 'transcribe' || result.isTextTranslation === isTextTranslation)
    );
    console.log('Filtered results:', filteredResults);
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {filteredResults.map((result: TranscriptionResult, index: number) => (
          <Box key={`${type}-${isTextTranslation ? 'text' : 'audio'}-${index}`} sx={{ 
            flex: '1 1 calc(50% - 12px)', 
            minWidth: '360px'
          }}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid',
                borderColor: result.error ? 'error.main' : 'divider',
                borderRadius: 3,
                position: 'relative',
                overflow: 'hidden',
                '&::before': result.error ? {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  bgcolor: 'error.main'
                } : {}
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start', 
                mb: 2.5,
                gap: 2
              }}>
                <Typography 
                  variant="subtitle1" 
                  component="h2" 
                  sx={{ 
                    fontWeight: 600,
                    color: result.error ? 'error.main' : 'text.primary'
                  }}
                >
                  {result.model}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {result.confidence && (
                    <Chip 
                      label={`${(result.confidence * 100).toFixed(1)}% Confidence`}
                      color="primary"
                      variant="outlined"
                      size="small"
                      sx={{ borderRadius: 2 }}
                    />
                  )}
                  {result.processingTime && (
                    <Chip 
                      label={`${result.processingTime.toFixed(1)}s`}
                      color="secondary"
                      variant="outlined"
                      size="small"
                      sx={{ borderRadius: 2 }}
                    />
                  )}
                  {qualityEvaluations[result.model] && (
                    <Chip 
                      label={`${(qualityEvaluations[result.model].score * 100).toFixed(1)}% Quality`}
                      title={qualityEvaluations[result.model].explanation}
                      color={qualityEvaluations[result.model].score > 0.8 ? "success" : "warning"}
                      variant="outlined"
                      size="small"
                      sx={{ borderRadius: 2 }}
                    />
                  )}
                </Box>
              </Box>
              
              <Typography 
                variant="body1" 
                sx={{ 
                  p: 2.5,
                  bgcolor: result.error ? 'error.main' : 'background.paper',
                  color: result.error ? 'white' : 'text.primary',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: result.error ? 'error.main' : 'divider',
                  flex: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                  lineHeight: 1.6
                }}
              >
                {result.text}
              </Typography>
              
              {qualityEvaluations[result.model]?.explanation && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mt: 2,
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    fontStyle: 'italic'
                  }}
                >
                  {qualityEvaluations[result.model].explanation}
                </Typography>
              )}
            </Paper>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 3
      }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Results
        </Typography>
        <Button
          variant="outlined"
          color="secondary"
          onClick={onClear}
          startIcon={<DeleteIcon />}
          sx={{ 
            borderRadius: 2,
            px: 3
          }}
        >
          Clear Results
        </Button>
      </Box>

      {audioBlob && (
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 4,
            background: 'linear-gradient(45deg, rgba(25, 118, 210, 0.05), rgba(66, 165, 245, 0.05))',
            backdropFilter: 'blur(10px)',
            border: '1px solid',
            borderColor: 'primary.main',
            borderRadius: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton 
              onClick={handlePlayPause} 
              color="primary"
              sx={{ 
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark',
                }
              }}
            >
              {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
            <VolumeUpIcon sx={{ color: 'text.secondary' }} />
            <Slider
              value={currentTime}
              min={0}
              max={duration || 100}
              onChange={handleTimeChange}
              step={0.1}
              sx={{ 
                flex: 1,
                '& .MuiSlider-thumb': {
                  width: 12,
                  height: 12,
                  transition: '0.2s',
                  '&:hover': {
                    boxShadow: '0 0 0 8px rgba(25, 118, 210, 0.16)',
                  },
                },
                '& .MuiSlider-rail': {
                  opacity: 0.32,
                }
              }}
            />
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              minWidth: 80,
              textAlign: 'right'
            }}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </Typography>
          </Box>
        </Paper>
      )}

      <Box sx={{ display: 'flex', gap: 4, mb: 4 }}>
        <Paper elevation={0} sx={{ 
          p: 4, 
          flex: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3
        }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Reference Text (Transcription)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={referenceText}
            onChange={handleReferenceChange}
            placeholder="Enter the correct transcription text here..."
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.paper',
                '&:hover': {
                  '& > fieldset': {
                    borderColor: 'primary.main',
                  }
                }
              }
            }}
          />
        </Paper>

        {results.some(result => result.type === 'translate') && (
          <Paper elevation={0} sx={{ 
            p: 4, 
            flex: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3
          }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 2 
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Reference Text (Translation)
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<TranslateIcon />}
                onClick={handleTranslateClick}
                disabled={!referenceText.trim() || isTranslating}
                sx={{ 
                  borderRadius: 2,
                  px: 3,
                  py: 1
                }}
              >
                {isTranslating ? 'Translating...' : 'Translate All'}
              </Button>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={translationReferenceText}
              onChange={handleTranslationReferenceChange}
              placeholder="Enter the correct translation text here..."
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.paper',
                  '&:hover': {
                    '& > fieldset': {
                      borderColor: 'primary.main',
                    }
                  }
                }
              }}
            />
          </Paper>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {/* Transcription Results */}
        <Box>
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ 
              ml: 1, 
              mb: 3,
              fontWeight: 600,
              color: 'text.primary'
            }}
          >
            Transcriptions
          </Typography>
          {renderResults('transcribe')}
        </Box>

        {/* Audio Translation Results */}
        {(() => {
          const hasAudioTranslations = results.some(
            result => result.type === 'translate' && !result.isTextTranslation
          );
          console.log('Has audio translations:', hasAudioTranslations);
          console.log('Translation results:', results.filter(r => r.type === 'translate'));
          return hasAudioTranslations && (
            <Box>
              <Typography 
                variant="h6" 
                gutterBottom 
                sx={{ 
                  ml: 1, 
                  mb: 3,
                  fontWeight: 600,
                  color: 'text.primary'
                }}
              >
                Translation (Audio to English)
              </Typography>
              {renderResults('translate', false)}
            </Box>
          );
        })()}

        {/* Text Translation Results */}
        {results.some(result => result.type === 'translate' && result.isTextTranslation) && (
          <Box>
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                ml: 1, 
                mb: 3,
                fontWeight: 600,
                color: 'text.primary'
              }}
            >
              Translation (Text to English)
            </Typography>
            {renderResults('translate', true)}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TranscriptionResults; 